import type { Vec3 } from '../contracts/game'
import type {
  BoxCollider,
  CapsuleCollider,
  OrientedBoxCollider,
  QuaternionData,
  SphereCollider,
  SweepHit,
} from './ColliderTypes'

const EPSILON = 1e-9

function dot(a: Readonly<Vec3>, b: Readonly<Vec3>): number {
  return a.x * b.x + a.y * b.y + a.z * b.z
}

function subtract(a: Readonly<Vec3>, b: Readonly<Vec3>): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z }
}

function addScaled(a: Readonly<Vec3>, b: Readonly<Vec3>, scale: number): Vec3 {
  return { x: a.x + b.x * scale, y: a.y + b.y * scale, z: a.z + b.z * scale }
}

function normalize(value: Readonly<Vec3>): Vec3 {
  const length = Math.hypot(value.x, value.y, value.z)
  if (length < EPSILON) return { x: 0, y: 1, z: 0 }
  return { x: value.x / length, y: value.y / length, z: value.z / length }
}

function rotateByQuaternion(value: Readonly<Vec3>, q: Readonly<QuaternionData>): Vec3 {
  const tx = 2 * (q.y * value.z - q.z * value.y)
  const ty = 2 * (q.z * value.x - q.x * value.z)
  const tz = 2 * (q.x * value.y - q.y * value.x)
  return {
    x: value.x + q.w * tx + q.y * tz - q.z * ty,
    y: value.y + q.w * ty + q.z * tx - q.x * tz,
    z: value.z + q.w * tz + q.x * ty - q.y * tx,
  }
}

function inverseRotate(value: Readonly<Vec3>, q: Readonly<QuaternionData>): Vec3 {
  return rotateByQuaternion(value, { x: -q.x, y: -q.y, z: -q.z, w: q.w })
}

export function sweepSphereAgainstBox(
  from: Readonly<Vec3>,
  to: Readonly<Vec3>,
  radius: number,
  box: BoxCollider,
): SweepHit | null {
  let entryTime = 0
  let exitTime = 1
  let hitAxis = 0
  let hitSign = 0
  const origins = [from.x, from.y, from.z]
  const directions = [to.x - from.x, to.y - from.y, to.z - from.z]
  const centers = [box.center.x, box.center.y, box.center.z]
  const extents = [box.halfExtents.x + radius, box.halfExtents.y + radius, box.halfExtents.z + radius]

  for (let axis = 0; axis < 3; axis += 1) {
    const origin = origins[axis]!
    const direction = directions[axis]!
    const minimum = centers[axis]! - extents[axis]!
    const maximum = centers[axis]! + extents[axis]!
    if (Math.abs(direction) < EPSILON) {
      if (origin < minimum || origin > maximum) return null
      continue
    }
    let near = (minimum - origin) / direction
    let far = (maximum - origin) / direction
    let sign = -Math.sign(direction)
    if (near > far) {
      const swap = near
      near = far
      far = swap
      sign = -sign
    }
    if (near > entryTime) {
      entryTime = near
      hitAxis = axis
      hitSign = sign
    }
    exitTime = Math.min(exitTime, far)
    if (entryTime > exitTime) return null
  }

  if (entryTime < 0 || entryTime > 1) return null
  const normal = { x: 0, y: 0, z: 0 }
  if (hitAxis === 0) normal.x = hitSign
  else if (hitAxis === 1) normal.y = hitSign
  else normal.z = hitSign
  return { time: entryTime, normal }
}

export function sweepSphereAgainstOrientedBox(
  from: Readonly<Vec3>,
  to: Readonly<Vec3>,
  radius: number,
  box: OrientedBoxCollider,
): SweepHit | null {
  const localFrom = inverseRotate(subtract(from, box.center), box.rotation)
  const localTo = inverseRotate(subtract(to, box.center), box.rotation)
  const localHit = sweepSphereAgainstBox(localFrom, localTo, radius, {
    center: { x: 0, y: 0, z: 0 },
    halfExtents: box.halfExtents,
  })
  if (localHit === null) return null
  return { time: localHit.time, normal: normalize(rotateByQuaternion(localHit.normal, box.rotation)) }
}

export function sweepSphereAgainstSphere(
  from: Readonly<Vec3>,
  to: Readonly<Vec3>,
  radius: number,
  sphere: SphereCollider,
): SweepHit | null {
  const origin = subtract(from, sphere.center)
  const direction = subtract(to, from)
  const combinedRadius = radius + sphere.radius
  const a = dot(direction, direction)
  const c = dot(origin, origin) - combinedRadius * combinedRadius
  if (c <= 0) return { time: 0, normal: normalize(origin) }
  if (a < EPSILON) return null
  const b = 2 * dot(origin, direction)
  const discriminant = b * b - 4 * a * c
  if (discriminant < 0) return null
  const time = (-b - Math.sqrt(discriminant)) / (2 * a)
  if (time < 0 || time > 1) return null
  const centerAtHit = addScaled(from, direction, time)
  return { time, normal: normalize(subtract(centerAtHit, sphere.center)) }
}

function nearestCapHit(
  from: Readonly<Vec3>,
  to: Readonly<Vec3>,
  radius: number,
  a: Readonly<Vec3>,
  b: Readonly<Vec3>,
): SweepHit | null {
  const first = sweepSphereAgainstSphere(from, to, radius, { center: { ...a }, radius: 0 })
  const second = sweepSphereAgainstSphere(from, to, radius, { center: { ...b }, radius: 0 })
  if (first === null) return second
  if (second === null) return first
  return first.time <= second.time ? first : second
}

export function sweepSphereAgainstCapsule(
  from: Readonly<Vec3>,
  to: Readonly<Vec3>,
  radius: number,
  capsule: CapsuleCollider,
): SweepHit | null {
  const localFrom = inverseRotate(subtract(from, capsule.center), capsule.rotation)
  const localTo = inverseRotate(subtract(to, capsule.center), capsule.rotation)
  const direction = subtract(localTo, localFrom)
  const a = { x: 0, y: -capsule.halfHeight, z: 0 }
  const b = { x: 0, y: capsule.halfHeight, z: 0 }
  const ba = subtract(b, a)
  const oa = subtract(localFrom, a)
  const combinedRadius = radius + capsule.radius
  const baba = dot(ba, ba)
  const bard = dot(ba, direction)
  const baoa = dot(ba, oa)
  const rdoa = dot(direction, oa)
  const oaoa = dot(oa, oa)
  const quadraticA = baba * dot(direction, direction) - bard * bard
  const quadraticB = baba * rdoa - baoa * bard
  const quadraticC = baba * oaoa - baoa * baoa - combinedRadius * combinedRadius * baba
  const discriminant = quadraticB * quadraticB - quadraticA * quadraticC

  let bodyHit: SweepHit | null = null
  if (Math.abs(quadraticA) > EPSILON && discriminant >= 0) {
    const time = (-quadraticB - Math.sqrt(discriminant)) / quadraticA
    const y = baoa + time * bard
    if (time >= 0 && time <= 1 && y > 0 && y < baba) {
      const point = addScaled(localFrom, direction, time)
      const closest = addScaled(a, ba, y / baba)
      bodyHit = { time, normal: normalize(subtract(point, closest)) }
    }
  }

  const caps = nearestCapHit(localFrom, localTo, combinedRadius, a, b)
  const localHit = bodyHit === null ? caps : caps === null || bodyHit.time <= caps.time ? bodyHit : caps
  if (localHit === null) return null
  return { time: localHit.time, normal: normalize(rotateByQuaternion(localHit.normal, capsule.rotation)) }
}
